import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Card, Field, Input, Label, Screen, Text } from "@andrum/ui";
import { useThemeStore } from "@/stores/theme-store";
import { useAuthStore } from "@/stores/auth-store";
import {
  loadUserPreferencesRemote,
  restartOnboardingRemote,
  signOut,
  updateDisplayName,
  updatePreferencesRemote,
} from "@/features/auth/api/auth-api";
import {
  fetchBillingStatus,
  type BillingStatus,
} from "@/features/subscription/api/billing-api";
import { clearEntitlementCache } from "@/lib/billing/entitlements";
import { isAuthEnforced } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";
import {
  loadNotificationPrefs,
  saveNotificationPrefs,
  type NotificationPrefs,
} from "@/lib/preferences/notification-prefs";
import { scheduleWeeklyReminder } from "@/features/notifications/api/push-api";
import {
  getLocale,
  setLocale,
  supportedLocales,
  type AppLocale,
} from "@/i18n";
import { InviteCard } from "@/features/referrals/components/InviteCard";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { submitFeedback } from "@/features/learn/api/feedback-api";
import type { GoalCode, LifeSituation, PathwayStyle } from "@andrum/schemas";
import { goalsNeedPathway } from "@andrum/schemas";
import {
  disconnectClientLink,
  fetchClientMessages,
  listClientLinks,
  sendClientMessage,
  type ClientLink,
} from "@/features/practice/api/practice-api";

type Tab = "account" | "prefs" | "more" | "invite" | "practice";

const GOAL_CODES: GoalCode[] = [
  "energy",
  "joy",
  "calm",
  "connection",
  "meaning",
  "alone_time",
];
const LIFE_VALUES: LifeSituation[] = [
  "working",
  "studying",
  "parental_leave",
  "parent_of_small_children",
  "between_jobs",
  "retired",
  "other",
  "prefer_not_to_say",
];
const PATHWAY_VALUES: PathwayStyle[] = ["social", "solo", "both"];

function asPathway(value: string | null): PathwayStyle | null {
  return value === "social" || value === "solo" || value === "both"
    ? value
    : null;
}

export function ProfileScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, setTheme } = useThemeStore();
  const { status, user, profile, clear, setProfile } = useAuthStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameOk, setNameOk] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(
    () => profile?.display_name ?? ""
  );
  const [notif, setNotif] = useState<NotificationPrefs | null>(null);
  const [notifNote, setNotifNote] = useState<string | null>(null);
  const [locale, setLocaleState] = useState<AppLocale>(() => getLocale());
  const [tab, setTab] = useState<Tab>("account");
  const [fbCat, setFbCat] = useState("idea");
  const [fbBody, setFbBody] = useState("");
  const [fbBusy, setFbBusy] = useState(false);
  const [fbNote, setFbNote] = useState<string | null>(null);
  const [redoBusy, setRedoBusy] = useState(false);
  const [redoNote, setRedoNote] = useState<string | null>(null);
  const [practiceLinks, setPracticeLinks] = useState<ClientLink[]>([]);
  const [practiceNote, setPracticeNote] = useState<string | null>(null);
  const [practiceBusy, setPracticeBusy] = useState(false);
  const [practiceMsgs, setPracticeMsgs] = useState<
    Array<{ id: string; senderRole: string; body: string; createdAt: string }>
  >([]);
  const [practiceMsgDraft, setPracticeMsgDraft] = useState("");
  const [prefGoals, setPrefGoals] = useState<GoalCode[]>([]);
  const [prefLife, setPrefLife] = useState<LifeSituation[]>([]);
  const [prefJoy, setPrefJoy] = useState<PathwayStyle | null>(null);
  const [prefCalm, setPrefCalm] = useState<PathwayStyle | null>(null);
  const [prefBusy, setPrefBusy] = useState(false);
  const [prefNote, setPrefNote] = useState<string | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    void loadNotificationPrefs().then(setNotif);
  }, []);

  useEffect(() => {
    if (status !== "authenticated") {
      setBilling(null);
      return;
    }
    setBillingLoading(true);
    void fetchBillingStatus()
      .then((s) => {
        setBilling(s);
        clearEntitlementCache();
      })
      .finally(() => setBillingLoading(false));
  }, [status]);

  useEffect(() => {
    if (status !== "authenticated" || tab !== "prefs") return;
    void loadUserPreferencesRemote().then((result) => {
      if (!result.ok) return;
      setPrefGoals(
        result.data.goals.filter((g): g is GoalCode =>
          (GOAL_CODES as string[]).includes(g)
        )
      );
      setPrefLife(
        result.data.lifeSituations.filter((s): s is LifeSituation =>
          (LIFE_VALUES as string[]).includes(s)
        )
      );
      setPrefJoy(asPathway(result.data.joyPathway));
      setPrefCalm(asPathway(result.data.calmPathway));
    });
  }, [status, tab]);

  useEffect(() => {
    if (status !== "authenticated" || tab !== "practice") return;
    void listClientLinks().then(setPracticeLinks);
    void fetchClientMessages().then(setPracticeMsgs);
  }, [status, tab]);

  useEffect(() => {
    setDisplayName(profile?.display_name ?? "");
  }, [profile?.display_name]);

  const handleSignOut = async () => {
    setError(null);
    setBusy(true);
    const result = await signOut();
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    clear();
    void navigate({ to: "/welcome" });
  };

  const handleSaveName = async () => {
    setNameError(null);
    setNameOk(null);
    if (status !== "authenticated") {
      setNameError(t("profile.loginToSaveName"));
      return;
    }
    setNameBusy(true);
    const result = await updateDisplayName(displayName);
    setNameBusy(false);
    if (!result.ok) {
      setNameError(result.message);
      return;
    }
    setProfile(result.data);
    setNameOk(t("profile.nameSaved"));
  };

  const toggleNotifs = async () => {
    if (!notif) return;
    const next = { ...notif, enabled: !notif.enabled };
    if (next.enabled && typeof Notification !== "undefined") {
      if (Notification.permission === "default") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          setNotifNote(t("profile.notifDenied"));
          return;
        }
      }
      if (Notification.permission === "denied") {
        setNotifNote(t("profile.notifDenied"));
        return;
      }
    }
    await saveNotificationPrefs(next);
    setNotif(next);
    setNotifNote(next.enabled ? t("profile.notifOn") : t("profile.notifOff"));
  };

  const setHour = async (hour: number) => {
    if (!notif) return;
    const next = { ...notif, hour };
    await saveNotificationPrefs(next);
    setNotif(next);
  };

  const handleLocale = (next: AppLocale) => {
    setLocale(next);
    setLocaleState(next);
  };

  const togglePrefGoal = (code: GoalCode) => {
    setPrefGoals((current) => {
      if (current.includes(code)) {
        return current.filter((g) => g !== code);
      }
      if (current.length >= 3) return current;
      return [...current, code];
    });
  };

  const togglePrefLife = (value: LifeSituation) => {
    setPrefLife((current) => {
      if (value === "prefer_not_to_say") {
        return current.includes("prefer_not_to_say")
          ? []
          : ["prefer_not_to_say"];
      }
      let next = current.filter((s) => s !== "prefer_not_to_say");
      if (next.includes(value)) {
        next = next.filter((s) => s !== value);
      } else if (next.length < 3) {
        next = [...next, value];
      }
      return next;
    });
  };

  const handleSavePreferences = async () => {
    if (status !== "authenticated") return;
    setPrefNote(null);
    setPrefBusy(true);
    const need = goalsNeedPathway(prefGoals);
    const result = await updatePreferencesRemote({
      goals: prefGoals,
      lifeSituations: prefLife,
      joyPathway: need.joy ? prefJoy : null,
      calmPathway: need.calm ? prefCalm : null,
    });
    setPrefBusy(false);
    if (!result.ok) {
      setPrefNote(result.message);
      return;
    }
    setProfile(result.data);
    useOnboardingStore.setState({
      goals: prefGoals,
      lifeSituations: prefLife,
      lifeSituation: prefLife[0],
      joyPathway: need.joy ? (prefJoy ?? undefined) : undefined,
      calmPathway: need.calm ? (prefCalm ?? undefined) : undefined,
    });
    setPrefNote(t("profile.prefsSaved"));
  };

  return (
    <Screen>
      <Text variant="title" as="h1" className="shrink-0">
        {t("profile.title")}
      </Text>

      <div className="flex shrink-0 gap-1 overflow-x-auto pb-0.5">
        {(
          [
            ["account", t("profile.displayName")],
            ["practice", t("practice.sectionTitle")],
            ["invite", t("invite.tab")],
            ["prefs", t("profile.prefsTab")],
            ["more", t("profile.help")],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={
              tab === id
                ? "shrink-0 rounded-full bg-primary px-3 py-2 text-xs font-medium text-primary-foreground"
                : "shrink-0 rounded-full bg-surface-muted px-3 py-2 text-xs font-medium text-foreground-muted"
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3 pb-1">
        {tab === "account" ? (
          <Card className="space-y-3 p-4">
            {user?.email && status === "authenticated" ? (
              <Text variant="caption">{user.email}</Text>
            ) : (
              <Text variant="body" className="font-medium">
                {status === "demo" ? t("auth.demoMode") : t("auth.login")}
              </Text>
            )}
            {status === "authenticated" ? (
              <div className="rounded-2xl border border-border bg-surface-muted/40 px-3.5 py-3 space-y-2">
                <Text variant="label">{t("profile.planLabel")}</Text>
                {billingLoading ? (
                  <Text variant="subtle" className="text-sm">
                    {t("loadingShort")}
                  </Text>
                ) : billing?.isPremium ? (
                  <>
                    <Text variant="body" className="text-sm font-medium text-primary">
                      {t("profile.planPlus")}
                    </Text>
                    <Text variant="caption" className="text-foreground-muted">
                      {billing.plan && billing.plan !== "free"
                        ? t("profile.planDetail", {
                            plan: billing.plan,
                            status: billing.status,
                          })
                        : t("paywall.youHavePlus")}
                      {billing.currentPeriodEnd
                        ? ` · ${t("profile.planUntil", {
                            date: new Date(
                              billing.currentPeriodEnd
                            ).toLocaleDateString(),
                          })}`
                        : ""}
                    </Text>
                    <Button
                      fullWidth
                      variant="outline"
                      className="min-h-10"
                      onClick={() =>
                        void navigate({
                          to: "/app/paywall",
                          search: { reason: "default" },
                        })
                      }
                    >
                      {t("profile.managePlus")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Text variant="body" className="text-sm font-medium">
                      {t("profile.planFree")}
                    </Text>
                    <Text variant="caption" className="leading-snug text-foreground-muted">
                      {t("profile.planFreeHint")}
                    </Text>
                    <Button
                      fullWidth
                      variant="soft"
                      className="min-h-10"
                      onClick={() =>
                        void navigate({
                          to: "/app/paywall",
                          search: { reason: "default" },
                        })
                      }
                    >
                      {t("profile.upgradePlus")}
                    </Button>
                  </>
                )}
              </div>
            ) : null}
            {status === "authenticated" || !isAuthEnforced() ? (
              <>
                <Field>
                  <Label htmlFor="display-name">{t("profile.displayName")}</Label>
                  <Input
                    id="display-name"
                    type="text"
                    autoComplete="nickname"
                    maxLength={80}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={t("auth.firstName")}
                    disabled={status !== "authenticated"}
                  />
                </Field>
                <Button
                  fullWidth
                  variant="soft"
                  loading={nameBusy}
                  disabled={status !== "authenticated"}
                  onClick={() => void handleSaveName()}
                >
                  {t("profile.saveName")}
                </Button>
                {nameError ? (
                  <p className="text-sm text-danger" role="alert">
                    {nameError}
                  </p>
                ) : null}
                {nameOk ? (
                  <p className="text-sm text-secondary" role="status">
                    {nameOk}
                  </p>
                ) : null}
              </>
            ) : null}
            {error ? (
              <p className="text-center text-sm text-danger" role="alert">
                {error}
              </p>
            ) : null}
            {status === "authenticated" || isAuthEnforced() ? (
              <Button
                fullWidth
                variant="outline"
                loading={busy}
                onClick={() => void handleSignOut()}
              >
                {t("profile.logout")}
              </Button>
            ) : null}
          </Card>
        ) : null}

        {tab === "practice" ? (
          <div className="space-y-3">
            {status !== "authenticated" ? (
              <Card className="space-y-3 p-4">
                <Text variant="body" className="text-sm leading-relaxed">
                  {t("invite.loginRequired")}
                </Text>
                <Button fullWidth onClick={() => void navigate({ to: "/login" })}>
                  {t("auth.login")}
                </Button>
              </Card>
            ) : (
              <>
                <Card className="space-y-3 p-4">
                  <Text variant="heading">{t("practice.sectionTitle")}</Text>
                  <Text variant="body" className="text-sm text-text-secondary">
                    {t("practice.shareHint")}
                  </Text>
                  {practiceLinks.filter((l) => l.status === "active").length ===
                  0 ? (
                    <Text variant="body" className="text-sm">
                      {t("practice.notLinked")}
                    </Text>
                  ) : (
                    practiceLinks
                      .filter((l) => l.status === "active")
                      .map((link) => (
                        <div key={link.linkId} className="space-y-2 border-t border-border pt-3">
                          <Text variant="body" className="font-medium">
                            {link.practiceName ?? t("practice.linkedGeneric")}
                          </Text>
                          {link.clinicianName ? (
                            <Text variant="caption">{link.clinicianName}</Text>
                          ) : null}
                          <Button
                            fullWidth
                            variant="outline"
                            loading={practiceBusy}
                            onClick={() => {
                              if (
                                !window.confirm(t("practice.disconnectConfirm"))
                              ) {
                                return;
                              }
                              setPracticeBusy(true);
                              void disconnectClientLink(link.linkId).then(
                                (r) => {
                                  setPracticeBusy(false);
                                  if (r.ok) {
                                    setPracticeNote(t("practice.disconnected"));
                                    void listClientLinks().then(
                                      setPracticeLinks
                                    );
                                  } else {
                                    setPracticeNote(r.message ?? t("errorGeneric"));
                                  }
                                }
                              );
                            }}
                          >
                            {t("practice.disconnect")}
                          </Button>
                        </div>
                      ))
                  )}
                  {practiceNote ? (
                    <p className="text-sm text-secondary" role="status">
                      {practiceNote}
                    </p>
                  ) : null}
                </Card>
                {practiceLinks.some((l) => l.status === "active") ? (
                  <Card className="space-y-3 p-4">
                    <Text variant="heading">{t("practice.messagesTitle")}</Text>
                    <div className="max-h-40 space-y-2 overflow-y-auto">
                      {practiceMsgs.length === 0 ? (
                        <Text variant="body" className="text-sm text-text-secondary">
                          {t("practice.messagesEmpty")}
                        </Text>
                      ) : (
                        practiceMsgs.map((m) => (
                          <div
                            key={m.id}
                            className="rounded-xl bg-surface-muted px-3 py-2 text-sm"
                          >
                            <span className="font-medium">
                              {m.senderRole === "client"
                                ? t("practice.you")
                                : t("practice.clinician")}
                              :{" "}
                            </span>
                            {m.body}
                          </div>
                        ))
                      )}
                    </div>
                    <Input
                      value={practiceMsgDraft}
                      onChange={(e) => setPracticeMsgDraft(e.target.value)}
                      placeholder={t("practice.messagePlaceholder")}
                      maxLength={2000}
                    />
                    <Button
                      fullWidth
                      variant="soft"
                      loading={practiceBusy}
                      disabled={!practiceMsgDraft.trim()}
                      onClick={() => {
                        const body = practiceMsgDraft.trim();
                        if (!body) return;
                        setPracticeBusy(true);
                        void sendClientMessage(body).then((r) => {
                          setPracticeBusy(false);
                          if (r.ok) {
                            setPracticeMsgDraft("");
                            void fetchClientMessages().then(setPracticeMsgs);
                          } else {
                            setPracticeNote(r.message ?? t("errorGeneric"));
                          }
                        });
                      }}
                    >
                      {t("practice.sendMessage")}
                    </Button>
                  </Card>
                ) : null}
                <Button
                  fullWidth
                  variant="soft"
                  onClick={() => void navigate({ to: "/join" })}
                >
                  {t("practice.enterCodeCta")}
                </Button>
              </>
            )}
          </div>
        ) : null}

        {tab === "invite" ? (
          <div>
            {status === "authenticated" ? (
              <InviteCard />
            ) : (
              <Card className="space-y-3 p-4">
                <Text variant="body" className="text-sm leading-relaxed">
                  {t("invite.loginRequired")}
                </Text>
                <Button fullWidth onClick={() => void navigate({ to: "/login" })}>
                  {t("auth.login")}
                </Button>
              </Card>
            )}
          </div>
        ) : null}

        {tab === "prefs" ? (
          <div className="space-y-3">
            {status === "authenticated" ? (
              <Card className="space-y-3 p-4">
                <Text variant="label">{t("profile.prefsTitle")}</Text>
                <Text variant="subtle" className="text-sm leading-snug">
                  {t("profile.prefsHint")}
                </Text>
                <Text variant="caption">{t("onboarding.goalsHint")}</Text>
                <div className="flex flex-wrap gap-1.5">
                  {GOAL_CODES.map((code) => (
                    <Button
                      key={code}
                      type="button"
                      variant={prefGoals.includes(code) ? "primary" : "soft"}
                      className="min-h-9 rounded-xl px-2.5 py-1 text-xs"
                      onClick={() => togglePrefGoal(code)}
                    >
                      {t(`onboarding.goals.${code}`)}
                    </Button>
                  ))}
                </div>
                {goalsNeedPathway(prefGoals).joy ? (
                  <div className="space-y-1.5">
                    <Text variant="caption">
                      {t("onboarding.pathwayJoyLabel")}
                    </Text>
                    <div className="flex flex-wrap gap-1.5">
                      {PATHWAY_VALUES.map((p) => (
                        <Button
                          key={`joy-${p}`}
                          type="button"
                          variant={prefJoy === p ? "secondary" : "soft"}
                          className="min-h-9 rounded-xl px-2.5 py-1 text-xs"
                          onClick={() => setPrefJoy(p)}
                        >
                          {t(`onboarding.pathway.${p}`)}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {goalsNeedPathway(prefGoals).calm ? (
                  <div className="space-y-1.5">
                    <Text variant="caption">
                      {t("onboarding.pathwayCalmLabel")}
                    </Text>
                    <div className="flex flex-wrap gap-1.5">
                      {PATHWAY_VALUES.map((p) => (
                        <Button
                          key={`calm-${p}`}
                          type="button"
                          variant={prefCalm === p ? "secondary" : "soft"}
                          className="min-h-9 rounded-xl px-2.5 py-1 text-xs"
                          onClick={() => setPrefCalm(p)}
                        >
                          {t(`onboarding.pathway.${p}`)}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : null}
                <Text variant="caption">{t("onboarding.lifeHint")}</Text>
                <div className="flex flex-wrap gap-1.5">
                  {LIFE_VALUES.map((value) => (
                    <Button
                      key={value}
                      type="button"
                      variant={prefLife.includes(value) ? "primary" : "soft"}
                      className="min-h-9 rounded-xl px-2.5 py-1 text-xs"
                      onClick={() => togglePrefLife(value)}
                    >
                      {t(`onboarding.life.${value}`)}
                    </Button>
                  ))}
                </div>
                <Button
                  fullWidth
                  variant="soft"
                  className="min-h-10"
                  loading={prefBusy}
                  disabled={prefGoals.length === 0}
                  onClick={() => void handleSavePreferences()}
                >
                  {t("profile.prefsSave")}
                </Button>
                {prefNote ? (
                  <Text variant="caption" className="text-secondary">
                    {prefNote}
                  </Text>
                ) : null}
              </Card>
            ) : null}

            <Card className="space-y-3 p-4">
              <Text variant="label">{t("profile.appSettingsTitle")}</Text>
              <Text variant="caption">{t("locale.label")}</Text>
              <div className="flex flex-wrap gap-1.5">
                {supportedLocales.map((code) => (
                  <Button
                    key={code}
                    type="button"
                    variant={locale === code ? "primary" : "soft"}
                    className={cn("min-h-10 rounded-xl px-2.5 py-1 text-xs")}
                    onClick={() => handleLocale(code)}
                  >
                    {t(`locale.${code}`)}
                  </Button>
                ))}
              </div>
              <Text variant="caption">{t("profile.theme")}</Text>
              <div className="flex flex-wrap gap-1.5">
                {(["light", "dark", "system"] as const).map((themeOption) => (
                  <Button
                    key={themeOption}
                    type="button"
                    variant={theme === themeOption ? "primary" : "soft"}
                    className={cn("min-h-10 rounded-xl px-2.5 py-1 text-xs")}
                    onClick={() => setTheme(themeOption)}
                  >
                    {themeOption === "light"
                      ? t("profile.themeLight")
                      : themeOption === "dark"
                        ? t("profile.themeDark")
                        : t("profile.themeSystem")}
                  </Button>
                ))}
              </div>
              <Text variant="caption">{t("profile.notifications")}</Text>
              <Button
                fullWidth
                variant={notif?.enabled ? "primary" : "soft"}
                className="min-h-10"
                onClick={() => void toggleNotifs()}
              >
                {notif?.enabled ? t("profile.notifOn") : t("profile.notifOff")}
              </Button>
              {notif?.enabled ? (
                <div className="flex flex-wrap gap-1.5">
                  {[8, 9, 12, 18, 20].map((h) => (
                    <Button
                      key={h}
                      type="button"
                      variant={notif.hour === h ? "secondary" : "ghost"}
                      className="min-h-9 rounded-xl px-2.5 py-1 text-xs"
                      onClick={() => void setHour(h)}
                    >
                      {h}:00
                    </Button>
                  ))}
                </div>
              ) : null}
              {notif?.enabled && status === "authenticated" ? (
                <Button
                  fullWidth
                  variant="outline"
                  className="min-h-10"
                  onClick={() => {
                    void (async () => {
                      const result = await scheduleWeeklyReminder(notif.hour);
                      setNotifNote(
                        result.ok ? t("insights.weekly") : result.message
                      );
                    })();
                  }}
                >
                  {t("insights.weekly")}
                </Button>
              ) : null}
              {notifNote ? <Text variant="caption">{notifNote}</Text> : null}
            </Card>
          </div>
        ) : null}

        {tab === "more" ? (
          <Card
            padding="none"
            elevation="flat"
            className="divide-y divide-border"
          >
            {status === "authenticated" ? (
              <div className="space-y-2 px-4 py-3">
                <Text variant="label">{t("profile.redoOnboarding")}</Text>
                <Text variant="subtle" className="text-sm leading-snug">
                  {t("profile.redoOnboardingHint")}
                </Text>
                <Button
                  fullWidth
                  variant="soft"
                  className="min-h-10"
                  loading={redoBusy}
                  onClick={() => {
                    void (async () => {
                      setRedoNote(null);
                      setRedoBusy(true);
                      const result = await restartOnboardingRemote();
                      setRedoBusy(false);
                      if (!result.ok) {
                        setRedoNote(result.message);
                        return;
                      }
                      setProfile(result.data);
                      useOnboardingStore.getState().reset();
                      void navigate({
                        to: "/onboarding",
                        search: { restart: true },
                      });
                    })();
                  }}
                >
                  {t("profile.redoOnboarding")}
                </Button>
                {redoNote ? (
                  <Text variant="caption" className="text-destructive">
                    {redoNote}
                  </Text>
                ) : null}
              </div>
            ) : null}
            <Link
              to="/app/privacy"
              className="block px-4 py-3 font-medium text-primary"
            >
              {t("profile.privacyCenter")}
            </Link>
            <Link
              to="/app/programs"
              className="block px-4 py-3 font-medium text-primary"
            >
              {t("profile.program21")}
            </Link>
            <Link
              to="/app/learn"
              className="block px-4 py-3 font-medium text-primary"
            >
              {t("profile.learnLink")}
            </Link>
            <Link
              to="/app/paywall"
              className="block px-4 py-3 font-medium text-primary"
            >
              {t("profile.andrumPlus")}
            </Link>
            <div className="space-y-2 px-4 py-3">
              <Text variant="label">{t("feedback.profileTitle")}</Text>
              <Text variant="subtle" className="text-sm leading-snug">
                {t("feedback.profileLead")}
              </Text>
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["idea", "catIdea"],
                    ["bug", "catBug"],
                    ["content", "catContent"],
                    ["praise", "catPraise"],
                    ["other", "catOther"],
                  ] as const
                ).map(([id, key]) => (
                  <Button
                    key={id}
                    type="button"
                    variant={fbCat === id ? "primary" : "soft"}
                    className="min-h-9 rounded-xl px-2.5 py-1 text-xs"
                    onClick={() => setFbCat(id)}
                  >
                    {t(`feedback.${key}`)}
                  </Button>
                ))}
              </div>
              <textarea
                value={fbBody}
                onChange={(e) => setFbBody(e.target.value.slice(0, 2000))}
                rows={3}
                placeholder={t("feedback.notePlaceholder")}
                className="w-full rounded-xl border border-border bg-surface-raised px-3 py-2 text-sm"
              />
              <Button
                fullWidth
                variant="secondary"
                className="min-h-10"
                loading={fbBusy}
                onClick={() => {
                  void (async () => {
                    setFbNote(null);
                    if (status !== "authenticated") {
                      setFbNote(t("feedback.loginRequired"));
                      return;
                    }
                    setFbBusy(true);
                    const result = await submitFeedback({
                      source: "profile",
                      category: fbCat,
                      body: fbBody.trim() || null,
                    });
                    setFbBusy(false);
                    if (result.ok) {
                      setFbBody("");
                      setFbNote(t("feedback.sent"));
                    } else {
                      setFbNote(t("feedback.error"));
                    }
                  })();
                }}
              >
                {t("feedback.send")}
              </Button>
              {fbNote ? <Text variant="caption">{fbNote}</Text> : null}
            </div>
            <div className="px-4 py-3">
              <Text variant="subtle">{t("profile.help")}</Text>
              <Text variant="body" className="mt-1 text-sm">
                {t("profile.helpBody")}
              </Text>
            </div>
            <Text variant="caption" className="px-4 py-3 text-center">
              {t("appName")}
            </Text>
          </Card>
        ) : null}
      </div>
    </Screen>
  );
}
